import {
  Grid,
  GridItem,
  Box,
  Heading,
  Text,
  HStack,
  Link,
  Button,
  Center,
  Flex,
  Progress,
  Stack,
  Badge,
  Tooltip,
  useColorMode,
} from "@chakra-ui/react";

import { Link as domlink } from "react-router-dom";
import { AiOutlineFilePpt } from "react-icons/ai";
import { MdOndemandVideo } from "react-icons/md";
import { VscBook } from "react-icons/vsc";
import { BsQuestionSquare } from "react-icons/bs";
import { FaClipboardList, FaEye } from "react-icons/fa";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import { LinkifyText } from "../../../lib/LinkifyText";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import incrementContentViewCountMutation from "../../../graphql/mutations/incrementContentViewCount";

const icons = [
  { type: "document", icon: <AiOutlineFilePpt mr="2" /> },
  { type: "video", icon: <MdOndemandVideo mr="2" /> },
  { type: "quiz", icon: <BsQuestionSquare mr="2" /> },
  { type: "survey", icon: <FaClipboardList mr="2" /> },
  { type: "link", icon: <ExternalLinkIcon mr="2" /> },
  { type: "homework", icon: <VscBook mr="2" /> },
];

const ContentCard = ({
  content,
  index,
  progress,
  doctype,
  handleUpdateProgress,
  alertJoincourse,
  openModal,
  setModalDisplay,
  subjectId,
  enroll,
  user,
}) => {
  const iconsContent = icons.find(({ type }) => type === content?.type);
  const { colorMode } = useColorMode();
  const { t } = useTranslation();
  const [incrementViewCount] = useMutation(incrementContentViewCountMutation);

  const isPublicContent = content?.public === true;

  const handleIncrementView = async () => {
    try {
      await incrementViewCount({ variables: { contentId: content._id } });
    } catch (err) {
      console.error('Error incrementing view count:', err);
    }
  };

  const handleCheckProgress = (content, value) => {
    if (!progress) {
      handleUpdateProgress(content, value);
    }
  };

  const handlePublicProgress = (value) => {
    if (enroll && user?._id) {
      handleCheckProgress(content, value);
    }
  };

  // Render button for public content (no login/enrollment required, tracks viewCount)
  const renderPublicContentButton = () => {
    const commonButtonProps = {
      w: "full",
      h: "auto",
      py: "2",
      justifyContent: "space-between",
    };

    const contentNameBox = (
      <Tooltip label={content?.name || "-"} hasArrow placement="top-start" openDelay={300}>
        <Box maxW={["220px", "320px", "480px"]} minW="0" overflow="hidden">
          <Text fontSize="15px" as="samp" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" display="block" textAlign="left">
            {content?.name}
          </Text>
        </Box>
      </Tooltip>
    );

    const viewCountBadge = (
      <HStack spacing={1}>
        <FaEye size={12} />
        <Text fontSize="13px" as="samp">{content?.viewCount || 0}</Text>
      </HStack>
    );

    switch (content?.type) {
      case "document":
        return (
          <Link
            onClick={() => {
              handleIncrementView();
              handlePublicProgress(100);
              setModalDisplay({
                name: content?.name,
                type: content?.type,
                link: content?.link,
                doctype: doctype,
              });
              openModal();
            }}
          >
            <Button {...commonButtonProps}>
              <HStack spacing="2">
                {iconsContent?.icon}
                {contentNameBox}
                <Badge colorScheme="green" fontSize="2xs">{t('course.free')}</Badge>
              </HStack>
              {viewCountBadge}
            </Button>
          </Link>
        );

      case "link":
        return (
          <Link
            href={content?.link}
            isExternal
            onClick={() => {
              handleIncrementView();
              handlePublicProgress(100);
            }}
          >
            <Button {...commonButtonProps}>
              <HStack spacing="2">
                {iconsContent?.icon}
                {contentNameBox}
                <Badge colorScheme="green" fontSize="2xs">{t('course.free')}</Badge>
              </HStack>
              {viewCountBadge}
            </Button>
          </Link>
        );

      case "video":
        return (
          <Link
            as={domlink}
            to={`/video/${content?._id}`}
            onClick={() => {
              handleIncrementView();
              handlePublicProgress(0);
            }}
          >
            <Button {...commonButtonProps}>
              <HStack spacing="2">
                {iconsContent?.icon}
                {contentNameBox}
                <Badge colorScheme="green" fontSize="2xs">{t('course.free')}</Badge>
              </HStack>
              {viewCountBadge}
            </Button>
          </Link>
        );

      default:
        // For other types (quiz, survey, homework) - still show as public but increment view
        const publicInternalPath =
          content?.type === "survey"
            ? `/subject/survey/${content?._id}`
            : content?.type === "homework"
            ? `/subject/sendhomework/${content?._id}`
            : content?.type === "quiz"
            ? `/subject/questiondetail/${content?._id}/${subjectId}`
            : null;

        return (
          <Link
            as={publicInternalPath ? domlink : undefined}
            to={publicInternalPath || undefined}
            onClick={() => {
              handleIncrementView();
            }}
          >
            <Button {...commonButtonProps}>
              <HStack spacing="2">
                {iconsContent?.icon}
                {contentNameBox}
                <Badge colorScheme="green" fontSize="2xs">{t('course.free')}</Badge>
              </HStack>
              {viewCountBadge}
            </Button>
          </Link>
        );
    }
  };

  // Centralized render logic for each content type button/link
  const renderContentButton = () => {
    const commonButtonProps = {
      w: "full",
      h: "auto",
      py: "2",
      justifyContent: "space-between",
    };

    const contentNameBox = (
      <Tooltip label={content?.name || "-"} hasArrow placement="top-start" openDelay={300}>
        <Box maxW={["220px", "320px", "480px"]} minW="0" overflow="hidden">
          <Text fontSize="15px" as="samp" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" display="block" textAlign="left">
            {content?.name}
          </Text>
        </Box>
      </Tooltip>
    );

    const progressText = (
      <Text fontSize="15px" as="samp">{`${progress} %`}</Text>
    );

    // Handle admin/teacher or enrolled users clickable logic
    // If content is public, allow anyone to view it (no login/enrollment required)
    if (isPublicContent) {
      // Public content: accessible by anyone
      return renderPublicContentButton();
    }

    if (!enroll && !(user?.role === "ADMIN" || user?.role === "TEACHER")) {
      // Not enrolled and not admin/teacher -> show alert join course
      return (
        <Link
          onClick={() => {
            alertJoincourse();
          }}
        >
          <Button {...commonButtonProps}>
            <HStack spacing="2">
              {iconsContent?.icon}
              {contentNameBox}
            </HStack>
            {progressText}
          </Button>
        </Link>
      );
    }

    // Enrolled users or admin/teacher user flow
    switch (content?.type) {
      case "document":
        return (
          <Link
            onClick={() => {
              setModalDisplay({
                name: content?.name,
                type: content?.type,
                link: content?.link,
                doctype: doctype,
              });
              if (enroll) {
                handleCheckProgress(content, 100);
              }
              openModal();
            }}
          >
            <Button {...commonButtonProps}>
              <HStack spacing="2">
                {iconsContent?.icon}
                {contentNameBox}
              </HStack>
              {progressText}
            </Button>
          </Link>
        );

      case "link":
        return (
          <Link
            href={content?.link}
            isExternal
            onClick={() => {
              if (enroll) {
                handleCheckProgress(content, 100);
              }
            }}
          >
            <Button {...commonButtonProps}>
              <HStack spacing="2">
                {iconsContent?.icon}
                {contentNameBox}
              </HStack>
              {progressText}
            </Button>
          </Link>
        );

      case "survey":
        return (
          <Link
            as={domlink}
            to={`/subject/survey/${content?._id}`}
          >
            <Button
              disabled={progress === 100}
              bg={progress === 100 ? "green.400" : "gray.200"}
              w="full"
              h="auto"
              px="4"
              py="2"
              textAlign="left"
              whiteSpace="normal"
            >
              <Flex w="full" align="start" justify="space-between" wrap="wrap">
                <HStack spacing="2" flex="1" minW="0" mr="4">
                  {iconsContent?.icon}
                  <Text
                    fontSize="15px"
                    as="samp"
                    whiteSpace="normal"
                    wordBreak="break-word"
                  >
                    {content?.name}
                  </Text>
                </HStack>
                <Text fontSize="15px" as="samp" whiteSpace="nowrap" mt="1">
                  {`${progress} %`}
                </Text>
              </Flex>
            </Button>
          </Link>
        );

      case "homework":
        return (
          <Link as={domlink} to={`/subject/sendhomework/${content?._id}`}>
            <Button {...commonButtonProps}>
              <HStack spacing="2">
                {iconsContent?.icon}
                {contentNameBox}
              </HStack>
              {progressText}
            </Button>
          </Link>
        );

      case "quiz":
        return (
          <Link
            as={domlink}
            to={`/subject/questiondetail/${content?._id}/${subjectId}`}
          >
            <Button disabled={progress === 100} {...commonButtonProps}>
              <HStack spacing="2">
                {iconsContent?.icon}
                {contentNameBox}
              </HStack>
              {progressText}
            </Button>
          </Link>
        );

      case "video":
        return (
          <Link
            as={domlink}
            to={`/video/${content?._id}`}
            onClick={() => {
              if (enroll) handleCheckProgress(content, 0);
            }}
          >
            <Button {...commonButtonProps}>
              <HStack spacing="2">
                {iconsContent?.icon}
                {contentNameBox}
              </HStack>
              {progressText}
            </Button>
          </Link>
        );

      default:
        // Default fallback for unknown content types
        return (
          <Button {...commonButtonProps} disabled>
            <HStack spacing="2">
              {iconsContent?.icon}
              {contentNameBox}
            </HStack>
            {progressText}
          </Button>
        );
    }
  };

  return (
    <Box maxW="7xl" mx={"auto"} pt={5} px={{ base: 2, sm: 12, md: 17 }}>
      <Grid
        boxShadow={"md"}
        templateRows="repeat(2, 1fr)"
        templateColumns="repeat(8, 1fr)"
        borderWidth={"2px"}
        borderRadius="xl"
      >
        <GridItem
          rowSpan={2}
          colSpan={1}
          bg={"primary.900"}
          borderLeftRadius="xl"
          w={"full"}
          h={"full"}
        >
          <Center mt={5}>
            <Stack>
              <Flex
                boxShadow={"lg"}
                borderWidth="5px"
                borderColor={colorMode === "light" ? "primary.400" : "brand.700"}
                w={14}
                h={14}
                align={"center"}
                justify={"center"}
                color={"primary.900"}
                rounded={"full"}
                bg={"white"}
                mb={1}
              >
                <Text fontSize={"18px"} as="samp">
                  {index + 1}
                </Text>
              </Flex>
            </Stack>
          </Center>
        </GridItem>
        <GridItem
          colSpan={7}
          p="2"
          bg={colorMode === "light" ? "brand.100" : "brand.700"}
          borderTopRightRadius="xl"
        >
          <Flex justify="space-between" align="center" py="3" ml={"5"} mr={"5"}>
            <HStack spacing={2} flex={1}>
              <Heading as="h4" size="md">
                {content?.name}
              </Heading>
              {isPublicContent && (
                <Badge colorScheme="green" fontSize="xs" variant="solid">
                  {t('course.free')}
                </Badge>
              )}
            </HStack>
            {(content?.viewCount > 0 || isPublicContent) && (
              <Tooltip label="จำนวนเข้าชม">
                <HStack spacing={1} color="gray.500">
                  <FaEye size={14} />
                  <Text fontSize="sm" fontWeight="bold">{content?.viewCount || 0}</Text>
                </HStack>
              </Tooltip>
            )}
          </Flex>
          <Text fontSize="sx" as="samp" ml={"5"}>
            {LinkifyText({ text: content?.carddescription }) || ""}
          </Text>
        </GridItem>
        <GridItem
          colSpan={7}
          p="2"
          bg={colorMode === "light" ? "brand.100" : "brand.700"}
          borderBottomRightRadius="xl"
        >
          <Box py="2">
            <Progress
              value={progress}
              borderRadius="lg"
              borderWidth="2px"
              hasStripe
              colorScheme="green"
            />
          </Box>
          <Box
            borderRadius="lg"
            borderWidth="2px"
            p={"2"}
            alignContent={"end"}
            boxShadow={"md"}
            bg={colorMode === "light" ? "white" : "brand.700"}
          >
            {renderContentButton()}
          </Box>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default ContentCard;
